// FacilityActivityTab — the Activity tab on the facility card, split into five
// sub-tabs (2026-07-23, P1 T4). Replaces the single merged feed.
//
//   Project work | RAG changes | Finance changes | Sub Changes | Change log
//
// Default sub-tab is Project work. Each panel loads lazily (only the active
// panel is mounted). Sources:
//   Project work  → projects WHERE facility_id
//   RAG changes   → v_facility_rag_history (via useRagHistory)
//   Finance chgs  → audit_log (facilities.monthly_rate) + audit_log
//                   (service_assignments sub-rate / service-line changes)
//   Sub Changes   → service_assignments (active + historical), derived
//   Change log    → audit_log for the facility, EXCLUDING rows already shown
//                   in RAG changes (account_status_rag) and Finance changes
//                   (monthly_rate) so nothing double-counts.
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { fmtDate } from '../../../shared/format';
import { fetchAmNameMap } from '../../../shared/am-name';
import { RAG_COLORS } from '../../../shared/design-tokens';
import { EmptyState, ErrorState, Skeleton } from '../../../shared/components';
import { useRagHistory } from '../hooks/use-rag-history';
import type { FacilityRagHistoryEntry, RagStatus } from '../../../shared/types';
import {
  NORMALIZED_LEGACY_BUCKETS,
  normalizeProjectServiceType,
} from '../../project-work/lib/service-types';
import { errorMessage } from '../../../shared/errors';

type SubTab = 'project' | 'rag' | 'finance' | 'sub' | 'changelog';

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'project', label: 'Projects & billing comms' },
  { id: 'rag', label: 'RAG changes' },
  { id: 'finance', label: 'Finance changes' },
  { id: 'sub', label: 'Sub Changes' },
  { id: 'changelog', label: 'Change log' },
];

const CONTROL_CLASS =
  'rounded-md border border-tcs-border bg-tcs-raised px-2.5 py-1.5 text-xs text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

function fmtMoney(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function monthsInPosition(startISO: string | null, endISO: string | null): number | null {
  if (!startISO) return null;
  const s = new Date(startISO);
  const e = endISO ? new Date(endISO) : new Date();
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  let m = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  if (e.getDate() < s.getDate()) m -= 1;
  return Math.max(0, m);
}

function fmtMonths(m: number | null): string {
  if (m == null) return '—';
  if (m < 1) return '<1 mo';
  return m === 1 ? '1 mo' : `${m} mos`;
}

export function FacilityActivityTab({ facilityId }: { facilityId: string }) {
  const [sub, setSub] = useState<SubTab>('project');
  return (
    <div className="space-y-4">
      <div className="-mx-4 overflow-x-auto px-4 md:mx-0 md:px-0">
        <div className="flex gap-1.5">
          {SUB_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSub(t.id)}
              className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
                sub === t.id
                  ? 'bg-tcs-inverse text-tcs-inverse-ink'
                  : 'border border-tcs-border bg-tcs-bg text-tcs-text-secondary hover:bg-tcs-bg-muted'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {sub === 'project' && <ProjectWorkPanel facilityId={facilityId} />}
      {sub === 'rag' && <RagChangesPanel facilityId={facilityId} />}
      {sub === 'finance' && <FinanceChangesPanel facilityId={facilityId} />}
      {sub === 'sub' && <SubChangesPanel facilityId={facilityId} />}
      {sub === 'changelog' && <ChangeLogPanel facilityId={facilityId} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4a — Project work (default)
// ---------------------------------------------------------------------------
interface ProjectRowLite {
  project_id: string;
  service_type_raw: string | null;
  bill_amount: number | null;
  pay_amount: number | null;
  assigned_partner_id: string | null;
  completion_date: string | null;
  start_date: string | null;
  notes: string | null;
  project_services: { assigned_partner_id: string | null; line_role: string | null }[] | null;
}

/**
 * Who did the work on a project, for this panel. TCSD-0380 (item #680): each
 * service line names its own sub, so the names come from the lines (a chargeback
 * line names the sub being CHARGED, so it is not "who did the work"). A
 * billing-comms note with no line names its sub on the record itself (Decision 2,
 * 2026-10-02), and only then is the project's own slot read.
 */
function projectSubIds(r: ProjectRowLite): string[] {
  const lines = r.project_services ?? [];
  if (lines.length === 0) return r.assigned_partner_id ? [r.assigned_partner_id] : [];
  return Array.from(
    new Set(
      lines
        .filter((l) => l.line_role !== 'chargeback')
        .map((l) => l.assigned_partner_id)
        .filter((v): v is string => !!v),
    ),
  );
}

function ProjectWorkPanel({ facilityId }: { facilityId: string }) {
  const [rows, setRows] = useState<ProjectRowLite[]>([]);
  const [subNames, setSubNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [subFilter, setSubFilter] = useState<string>('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('projects')
        .select(
          'project_id, service_type_raw, bill_amount, pay_amount, assigned_partner_id, completion_date, start_date, notes, project_services(assigned_partner_id, line_role)',
        )
        .eq('facility_id', facilityId);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setLoading(false);
        return;
      }
      const list = (data ?? []) as ProjectRowLite[];
      setRows(list);
      const ids = Array.from(
        new Set(list.flatMap(projectSubIds)),
      );
      if (ids.length > 0) {
        const { data: provs } = await supabase
          .from('service_providers')
          .select('provider_id, partner_name')
          .in('provider_id', ids);
        if (!cancelled) {
          const m = new Map<string, string>();
          for (const p of (provs ?? []) as Array<{ provider_id: string; partner_name: string | null }>) {
            if (p.partner_name) m.set(p.provider_id, p.partner_name);
          }
          setSubNames(m);
        }
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const enriched = useMemo(
    () =>
      rows
        .map((r) => ({
          ...r,
          _date: r.completion_date ?? r.start_date ?? null,
          _type: normalizeProjectServiceType(r.service_type_raw),
          _subNames: projectSubIds(r).map((id) => subNames.get(id) ?? id),
        }))
        .sort((a, b) => {
          const ta = a._date ? new Date(a._date).getTime() : 0;
          const tb = b._date ? new Date(b._date).getTime() : 0;
          return tb - ta;
        }),
    [rows, subNames],
  );

  const subOptions = useMemo(() => {
    const s = new Set<string>();
    for (const r of enriched) for (const n of r._subNames) s.add(n);
    return Array.from(s).sort();
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((r) => {
      if (typeFilter !== 'All' && r._type !== typeFilter) return false;
      if (subFilter !== 'All' && !r._subNames.includes(subFilter)) return false;
      if (q) {
        const hay = `${r.service_type_raw ?? ''} ${r.notes ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, typeFilter, subFilter, search]);

  if (loading) return <PanelSkeleton />;
  if (error) return <ErrorState title="Couldn't load project work" body={error} />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No project work yet"
        body="One-off jobs (strip & wax, carpet, deep cleans, etc.) logged for this facility appear here."
      />
    );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <select className={CONTROL_CLASS} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="All">All types</option>
          {NORMALIZED_LEGACY_BUCKETS.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <select className={CONTROL_CLASS} value={subFilter} onChange={(e) => setSubFilter(e.target.value)}>
          <option value="All">All subs</option>
          {subOptions.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          className={CONTROL_CLASS}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search type / notes…"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="rounded-xl border border-dashed border-tcs-border bg-tcs-surface p-4 text-xs text-tcs-text-muted">
          No project work matches the current filters.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
              <tr className="border-b border-tcs-border">
                <th className="py-2 pr-3 font-medium">Date</th>
                <th className="py-2 pr-3 font-medium">Type</th>
                <th className="py-2 pr-3 font-medium">Client rate</th>
                <th className="py-2 pr-3 font-medium">Contract rate</th>
                <th className="py-2 pr-3 font-medium">Sub</th>
                <th className="py-2 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.project_id} className="border-b border-tcs-border/60 align-top">
                  <td className="whitespace-nowrap py-2 pr-3 text-tcs-text-secondary">{fmtDate(r._date)}</td>
                  <td className="py-2 pr-3 text-tcs-text">{r._type}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-tcs-text">{fmtMoney(r.bill_amount)}</td>
                  <td className="whitespace-nowrap py-2 pr-3 text-tcs-text">{fmtMoney(r.pay_amount)}</td>
                  <td className="py-2 pr-3 text-tcs-text-secondary">{r._subNames.length ? r._subNames.join(', ') : '—'}</td>
                  <td className="py-2 text-tcs-text-secondary">{r.notes ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function PanelSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton width="100%" height={40} block />
      <Skeleton width="100%" height={40} block />
      <Skeleton width="100%" height={40} block />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4b — RAG changes (reuses v_facility_rag_history)
// ---------------------------------------------------------------------------
function RagChangesPanel({ facilityId }: { facilityId: string }) {
  const { entries, loading, error } = useRagHistory(facilityId);
  if (loading) return <PanelSkeleton />;
  if (error) return <ErrorState title="Couldn't load RAG history" body={error} />;
  if (entries.length === 0)
    return <EmptyState title="No RAG changes" body="Status changes for this facility appear here." />;

  const dot = (s: string | null | undefined) => (
    <span
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: s ? RAG_COLORS[s as RagStatus] ?? '#D1D5DB' : '#D1D5DB' }}
      aria-hidden="true"
    />
  );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
          <tr className="border-b border-tcs-border">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Change</th>
            <th className="py-2 pr-3 font-medium">Reason</th>
            <th className="py-2 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {(entries as FacilityRagHistoryEntry[]).map((e, i) => (
            <tr key={`${e.set_at}-${i}`} className="border-b border-tcs-border/60 align-top">
              <td className="whitespace-nowrap py-2 pr-3 text-tcs-text-secondary">{fmtDate(e.set_at)}</td>
              <td className="py-2 pr-3">
                <span className="inline-flex items-center gap-1.5">
                  {dot(e.rag_status_old)}
                  <span className="text-tcs-text-secondary">{e.rag_status_old ?? '—'}</span>
                  <span aria-hidden="true">→</span>
                  {dot(e.rag_status_new)}
                  <span className="font-medium text-tcs-text">{e.rag_status_new ?? '—'}</span>
                </span>
              </td>
              <td className="py-2 pr-3 text-tcs-text-secondary">{e.rag_reason ?? '—'}</td>
              <td className="py-2 text-tcs-text-secondary">{e.set_by_name ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4c — Finance changes (simplified view of audit_log — not every invoice)
// ---------------------------------------------------------------------------
interface AuditLite {
  audit_id: number;
  table_name: string;
  action: string;
  record_id: string;
  changed_fields: string[] | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  changed_by_employee_id: string | null;
  changed_at: string;
}

type FinanceKind = 'Client rate' | 'Contract rate' | 'Service line';
interface FinanceItem {
  key: string;
  kind: FinanceKind;
  ts: number;
  changed_at: string;
  value: string;
  by: string | null;
}

const SUB_RATE_FIELDS = ['sub_rate_monthly', 'sub_hourly_rate'];
const SERVICE_LINE_FIELDS = ['service_type', 'billing_method', 'billing_rate', 'invoice_to'];

function fmtJsonVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  return typeof v === 'object' ? JSON.stringify(v) : String(v);
}

function FinanceChangesPanel({ facilityId }: { facilityId: string }) {
  const [items, setItems] = useState<FinanceItem[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // 1. Client-rate changes: facilities.monthly_rate audit rows.
        const facRes = await supabase
          .from('audit_log')
          .select(
            'audit_id, table_name, action, record_id, changed_fields, old_data, new_data, changed_by_employee_id, changed_at',
          )
          .eq('record_id', facilityId)
          .eq('table_name', 'facilities')
          .order('changed_at', { ascending: false });
        if (facRes.error) throw facRes.error;

        // 2. Assignment ids for this facility, then their audit rows.
        const saIdsRes = await supabase
          .from('service_assignments')
          .select('assignment_id')
          .eq('facility_id', facilityId);
        if (saIdsRes.error) throw saIdsRes.error;
        const assignmentIds = ((saIdsRes.data ?? []) as Array<{ assignment_id: string }>).map(
          (r) => r.assignment_id,
        );
        let saAudit: AuditLite[] = [];
        if (assignmentIds.length > 0) {
          const saRes = await supabase
            .from('audit_log')
            .select(
              'audit_id, table_name, action, record_id, changed_fields, old_data, new_data, changed_by_employee_id, changed_at',
            )
            .eq('table_name', 'service_assignments')
            .in('record_id', assignmentIds)
            .order('changed_at', { ascending: false });
          if (saRes.error) throw saRes.error;
          saAudit = (saRes.data ?? []) as unknown as AuditLite[];
        }
        if (cancelled) return;

        const out: FinanceItem[] = [];
        const facRows = (facRes.data ?? []) as unknown as AuditLite[];
        for (const r of facRows) {
          const cf = r.changed_fields ?? [];
          if (r.action === 'UPDATE' && cf.includes('monthly_rate')) {
            out.push({
              key: `f-${r.audit_id}`,
              kind: 'Client rate',
              ts: new Date(r.changed_at).getTime(),
              changed_at: r.changed_at,
              value: `${fmtJsonVal(r.old_data?.monthly_rate)} → ${fmtJsonVal(r.new_data?.monthly_rate)}`,
              by: r.changed_by_employee_id,
            });
          }
        }
        for (const r of saAudit) {
          const cf = r.changed_fields ?? [];
          const isSubRate = r.action === 'UPDATE' && cf.some((f) => SUB_RATE_FIELDS.includes(f));
          const isServiceLine =
            r.action === 'INSERT' ||
            r.action === 'DELETE' ||
            (r.action === 'UPDATE' && cf.some((f) => SERVICE_LINE_FIELDS.includes(f)));
          if (isSubRate) {
            const field = cf.find((f) => SUB_RATE_FIELDS.includes(f)) ?? 'sub_rate_monthly';
            out.push({
              key: `s-${r.audit_id}`,
              kind: 'Contract rate',
              ts: new Date(r.changed_at).getTime(),
              changed_at: r.changed_at,
              value: `${fmtJsonVal(r.old_data?.[field])} → ${fmtJsonVal(r.new_data?.[field])}`,
              by: r.changed_by_employee_id,
            });
          } else if (isServiceLine) {
            const label =
              r.action === 'INSERT'
                ? `Added: ${fmtJsonVal(r.new_data?.service_type)}`
                : r.action === 'DELETE'
                  ? `Removed: ${fmtJsonVal(r.old_data?.service_type)}`
                  : `${fmtJsonVal(r.old_data?.service_type)} → ${fmtJsonVal(r.new_data?.service_type)}`;
            out.push({
              key: `l-${r.audit_id}`,
              kind: 'Service line',
              ts: new Date(r.changed_at).getTime(),
              changed_at: r.changed_at,
              value: label,
              by: r.changed_by_employee_id,
            });
          }
        }
        out.sort((a, b) => b.ts - a.ts);
        setItems(out);
        const map = await fetchAmNameMap(out.map((i) => i.by));
        if (!cancelled) setNames(map);
      } catch (e) {
        if (!cancelled) setError(errorMessage(e, 'Failed to load finance changes'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  if (loading) return <PanelSkeleton />;
  if (error) return <ErrorState title="Couldn't load finance changes" body={error} />;
  if (items.length === 0)
    return (
      <EmptyState
        title="No finance changes"
        body="Client-rate, sub-rate, and service-line changes appear here (invoices are excluded)."
      />
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
          <tr className="border-b border-tcs-border">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Change type</th>
            <th className="py-2 pr-3 font-medium">Value</th>
            <th className="py-2 font-medium">By</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i) => (
            <tr key={i.key} className="border-b border-tcs-border/60 align-top">
              <td className="whitespace-nowrap py-2 pr-3 text-tcs-text-secondary">{fmtDate(i.changed_at)}</td>
              <td className="py-2 pr-3 text-tcs-text">{i.kind}</td>
              <td className="py-2 pr-3 text-tcs-text-secondary">{i.value}</td>
              <td className="py-2 text-tcs-text-secondary">
                {(i.by && names.get(i.by)) || i.by || 'system'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4d — Sub Changes (derived from service_assignments)
// ---------------------------------------------------------------------------
interface SaLite {
  assignment_id: string;
  provider_id: string | null;
  assignment_status: string | null;
  start_date: string | null;
  notes: string | null;
  replaced_by_replacement_id: string | null;
}

function SubChangesPanel({ facilityId }: { facilityId: string }) {
  const [rows, setRows] = useState<SaLite[]>([]);
  const [subNames, setSubNames] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('service_assignments')
        .select('assignment_id, provider_id, assignment_status, start_date, notes, replaced_by_replacement_id')
        .eq('facility_id', facilityId);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setLoading(false);
        return;
      }
      const list = (data ?? []) as SaLite[];
      setRows(list);
      const ids = Array.from(new Set(list.map((r) => r.provider_id).filter((v): v is string => !!v)));
      if (ids.length > 0) {
        const { data: provs } = await supabase
          .from('service_providers')
          .select('provider_id, partner_name')
          .in('provider_id', ids);
        if (!cancelled) {
          const m = new Map<string, string>();
          for (const p of (provs ?? []) as Array<{ provider_id: string; partner_name: string | null }>) {
            if (p.partner_name) m.set(p.provider_id, p.partner_name);
          }
          setSubNames(m);
        }
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const display = useMemo(() => {
    // Ascending by start_date to derive each row's "end" as the next start.
    const asc = [...rows].sort((a, b) => {
      const ta = a.start_date ? new Date(a.start_date).getTime() : 0;
      const tb = b.start_date ? new Date(b.start_date).getTime() : 0;
      return ta - tb;
    });
    const withTenure = asc.map((r, idx) => {
      const isActive = r.assignment_status === 'Active';
      const end = isActive ? null : asc[idx + 1]?.start_date ?? null;
      return {
        ...r,
        _isActive: isActive,
        _subName: r.provider_id ? subNames.get(r.provider_id) ?? r.provider_id : '—',
        _months: monthsInPosition(r.start_date, end),
        _reason: r.notes ?? (r.replaced_by_replacement_id ? 'Replaced' : '—'),
      };
    });
    // Current sub(s) pinned to top; the rest newest-first by start_date.
    const active = withTenure.filter((r) => r._isActive);
    const rest = withTenure
      .filter((r) => !r._isActive)
      .sort((a, b) => {
        const ta = a.start_date ? new Date(a.start_date).getTime() : 0;
        const tb = b.start_date ? new Date(b.start_date).getTime() : 0;
        return tb - ta;
      });
    return [...active, ...rest];
  }, [rows, subNames]);

  if (loading) return <PanelSkeleton />;
  if (error) return <ErrorState title="Couldn't load sub changes" body={error} />;
  if (rows.length === 0)
    return <EmptyState title="No sub assignments" body="Subcontractor assignments for this facility appear here." />;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-xs">
        <thead className="text-[10px] uppercase tracking-wide text-tcs-text-muted">
          <tr className="border-b border-tcs-border">
            <th className="py-2 pr-3 font-medium">Date</th>
            <th className="py-2 pr-3 font-medium">Sub</th>
            <th className="py-2 pr-3 font-medium">Time in position</th>
            <th className="py-2 font-medium">Reason for change</th>
          </tr>
        </thead>
        <tbody>
          {display.map((r) => (
            <tr key={r.assignment_id} className="border-b border-tcs-border/60 align-top">
              <td className="whitespace-nowrap py-2 pr-3 text-tcs-text-secondary">{fmtDate(r.start_date)}</td>
              <td className="py-2 pr-3">
                <span className="font-medium text-tcs-text">{r._subName}</span>
                {r._isActive && (
                  <span className="ml-2 inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                    current
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap py-2 pr-3 text-tcs-text-secondary">{fmtMonths(r._months)}</td>
              <td className="py-2 text-tcs-text-secondary">{r._reason}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4e — Change log (remainder only)
// ---------------------------------------------------------------------------
const CHANGELOG_PAGE = 20;
// Fields already surfaced in RAG changes / Finance changes — excluded here so
// the Change log doesn't double-count.
const EXCLUDED_FACILITY_FIELDS = ['account_status_rag', 'monthly_rate'];

function tableLabel(t: string): string | null {
  if (t === 'facilities') return null;
  return t.replace(/^facility_/, '').replace(/_/g, ' ');
}
function fmtVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  let s: string;
  if (typeof v === 'object') {
    try {
      s = JSON.stringify(v);
    } catch {
      s = String(v);
    }
  } else s = String(v);
  return s.length > 120 ? `${s.slice(0, 117)}…` : s;
}

function ChangeLogPanel({ facilityId }: { facilityId: string }) {
  const [rows, setRows] = useState<AuditLite[]>([]);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const { data, error: err } = await supabase
        .from('audit_log')
        .select(
          'audit_id, table_name, action, record_id, changed_fields, old_data, new_data, changed_by_employee_id, changed_at',
        )
        .eq('record_id', facilityId)
        .order('changed_at', { ascending: false })
        .range(0, 999);
      if (cancelled) return;
      if (err) {
        setError(err.message);
        setRows([]);
        setLoading(false);
        return;
      }
      const all = (data ?? []) as unknown as AuditLite[];
      // Remainder: drop facilities rows whose only tracked change was a RAG
      // status or the monthly_rate (already shown in the other sub-tabs).
      const remainder = all.filter((r) => {
        if (r.table_name === 'facilities' && r.action === 'UPDATE') {
          const cf = r.changed_fields ?? [];
          if (cf.length > 0 && cf.every((f) => EXCLUDED_FACILITY_FIELDS.includes(f))) return false;
        }
        return true;
      });
      setRows(remainder);
      setPage(0);
      try {
        const map = await fetchAmNameMap(remainder.map((r) => r.changed_by_employee_id));
        if (!cancelled) setNames(map);
      } catch {
        /* name resolution is cosmetic */
      }
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  const pageCount = Math.max(1, Math.ceil(rows.length / CHANGELOG_PAGE));
  const pageRows = rows.slice(page * CHANGELOG_PAGE, page * CHANGELOG_PAGE + CHANGELOG_PAGE);
  const who = (r: AuditLite) =>
    (r.changed_by_employee_id && names.get(r.changed_by_employee_id)) ||
    r.changed_by_employee_id ||
    'system';

  if (loading) return <PanelSkeleton />;
  if (error) return <ErrorState title="Couldn't load change log" body={error} />;
  if (rows.length === 0)
    return (
      <EmptyState
        title="No other changes"
        body="Remaining field-level edits (outside RAG and finance) appear here."
      />
    );

  return (
    <div className="space-y-3">
      {pageCount > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-tcs-text-secondary">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="rounded-md border border-tcs-border px-2 py-1 hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            ← Prev
          </button>
          <span>
            {page + 1} / {pageCount}
          </span>
          <button
            type="button"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            className="rounded-md border border-tcs-border px-2 py-1 hover:bg-tcs-bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next →
          </button>
        </div>
      )}
      <ol className="space-y-2">
        {pageRows.map((r) => {
          const label = tableLabel(r.table_name);
          const fields = r.action === 'UPDATE' ? r.changed_fields ?? [] : [];
          return (
            <li key={r.audit_id} className="rounded-xl border border-tcs-border bg-tcs-bg p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs text-tcs-text-muted">
                <span className="font-medium text-tcs-text">{who(r)}</span>
                <span aria-hidden="true">·</span>
                <span>{fmtDate(r.changed_at)}</span>
                {label && (
                  <span className="inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                    {label}
                  </span>
                )}
                {r.action !== 'UPDATE' && (
                  <span className="inline-flex items-center rounded bg-tcs-bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-tcs-text-secondary">
                    {r.action === 'INSERT' ? 'created' : r.action.toLowerCase()}
                  </span>
                )}
              </div>
              {fields.length > 0 && (
                <dl className="mt-2 space-y-1">
                  {fields.map((f) => (
                    <div key={f} className="flex flex-wrap items-baseline gap-x-2 text-xs">
                      <dt className="font-mono font-medium text-tcs-text-secondary">{f}</dt>
                      <dd className="text-tcs-text-muted">
                        <span className="line-through decoration-tcs-border-strong">
                          {fmtVal(r.old_data?.[f])}
                        </span>{' '}
                        <span aria-hidden="true">→</span>{' '}
                        <span className="text-tcs-text">{fmtVal(r.new_data?.[f])}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
